import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { SurfaceToken } from "../../components/ui/box-classes";
import type { WidgetConfig } from "./types";

/**
 * The kit's per-slot instance context: which placed widget am I (`instanceId`
 * + persisted `config`)? Provided automatically by `WidgetGrid` around every
 * widget slot; hosts that mount a bare `<Widget>` for a real placed widget
 * provide it themselves.
 *
 * Domain semantics (what data situation the widget is in) are NOT here — a
 * consuming domain layers its own context alongside this one.
 */

export type WidgetInstanceContextValue = {
  /** The placed widget's persisted identity. */
  instanceId: string;
  /** The widget's persisted config blob (raw, pre-resolution). */
  config: WidgetConfig | null;
};

const WidgetInstanceContext = createContext<WidgetInstanceContextValue | null>(null);

/** Provide one placed widget's identity. `WidgetGrid` mounts this per slot. */
export function WidgetInstanceProvider({
  instanceId,
  config,
  children,
}: {
  instanceId: string;
  config: WidgetConfig | null;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ instanceId, config }), [instanceId, config]);
  return <WidgetInstanceContext.Provider value={value}>{children}</WidgetInstanceContext.Provider>;
}

/**
 * The wrapping placed widget's identity, or `null` outside any provider (a
 * widget body rendered with no placement — e.g. a settings preview). Never
 * throws.
 */
export function useWidgetInstance(): WidgetInstanceContextValue | null {
  return useContext(WidgetInstanceContext);
}

/**
 * The surface the wrapping `WidgetCard` painted — what a body's own opaque
 * parts (a table's sticky header, pinned cells) must paint to blend with the
 * card instead of cutting a differently-filled hole. `WidgetCard` provides
 * its ONE resolved value (the surface it paints, or the board's surface for a
 * canvas card); outside any card the body sits on the page.
 */
const WidgetSurfaceContext = createContext<SurfaceToken>("background");

export function WidgetSurfaceProvider({
  value,
  children,
}: {
  value: SurfaceToken;
  children: ReactNode;
}) {
  return <WidgetSurfaceContext.Provider value={value}>{children}</WidgetSurfaceContext.Provider>;
}

/** The surface the wrapping widget card painted (`background` outside a card). */
export function useWidgetSurface(): SurfaceToken {
  return useContext(WidgetSurfaceContext);
}
